import React, { useState } from 'react';
import { Save, Building2, Upload, Globe, Palette, MapPin } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ColorPicker } from '../../../components/ui/ColorPicker';
import { WilayaSelector } from '../../../components/ui/WilayaSelector';
import { useAuth } from '../../../contexts/AuthContext';
import { useAgencies } from '../../../hooks/useAgencies';

export const AgencySettings: React.FC = () => {
  const { user } = useAuth();
  const { agencies, updateAgency } = useAgencies();
  const [logo, setLogo] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // Récupérer les données de l'agence connectée
  const currentAgency = agencies.find(agency => agency.id === user?.agency_id?.toString());
  
  const [formData, setFormData] = useState({
    name: currentAgency?.name || '',
    address: currentAgency?.address || '',
    city: currentAgency?.city || '',
    wilaya: currentAgency?.wilaya || '',
    contact_phone: '',
    contact_email: '',
    website_url: currentAgency?.website_url || '',
    subscription_plan: '',
    brand_color: currentAgency?.brand_color || '#0a8345',
  });

  // Mettre à jour le formulaire quand l'agence change
  React.useEffect(() => {
    if (currentAgency) {
      console.log('🏢 Agence trouvée:', currentAgency);
      setFormData({
        name: currentAgency.name || '',
        address: currentAgency.address || '',
        city: currentAgency.city || '',
        wilaya: currentAgency.wilaya || '',
        contact_phone: currentAgency.phone || '',
        contact_email: currentAgency.email || '',
        website_url: currentAgency.website_url || '',
        subscription_plan: currentAgency.subscription?.plan || 'basic',
        brand_color: currentAgency.brand_color || '#0a8345',
      });
    }
  }, [currentAgency]);

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Le fichier est trop volumineux. Maximum 2MB autorisé.');
        return;
      }
      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        alert('Format de fichier non supporté. Utilisez PNG ou JPG.');
        return;
      }
      setLogo(file);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSave = async () => {
    if (!currentAgency) return;
    
    setIsSaving(true);
    try {
      // Gérer l'upload du logo en premier si un nouveau logo a été sélectionné
      let logoUrl = currentAgency.logo_url;
      if (logo) {
        console.log('📤 Upload du nouveau logo...');
        try {
          const logoFormData = new FormData();
          logoFormData.append('logo', logo);
          
          const logoResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/agencies/${currentAgency.id}/upload-logo`, {
            method: 'POST',
            body: logoFormData,
          });
          
          if (logoResponse.ok) {
            const logoResult = await logoResponse.json();
            logoUrl = logoResult.logo_url;
            console.log('✅ Logo uploadé avec succès:', logoUrl);
          } else {
            const logoError = await logoResponse.json();
            console.error('❌ Erreur upload logo:', logoError);
            throw new Error(logoError.error || 'Erreur lors de l\'upload du logo');
          }
        } catch (logoError) {
          console.error('❌ Erreur upload logo:', logoError);
          throw new Error('Erreur lors de l\'upload du logo');
        }
      }

      // Préparer les données à envoyer
      const updateData = {
        name: formData.name,
        address: formData.address,
        city: formData.city,
        wilaya: formData.wilaya,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        website_url: formData.website_url,
        brand_color: formData.brand_color,
        subscription_plan: formData.subscription_plan,
        logo_url: logoUrl,
      };

      console.log('💾 Sauvegarde des données agence:', updateData);
      
      const success = await updateAgency(currentAgency.id, updateData);
      
      if (success) {
        // Réinitialiser le logo sélectionné après sauvegarde
        setLogo(null);
        alert('Paramètres sauvegardés avec succès');
      } else {
        alert('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(`Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorChange = (color: string) => {
    console.log('🎨 AgencySettings.handleColorChange appelé avec:', color);
    setFormData(prev => ({ ...prev, brand_color: color }));
  };

  const handleColorSave = (color: string) => {
    console.log('🎨 AgencySettings.handleColorSave appelé avec:', color);

    // Sauvegarder automatiquement la couleur en base
    if (currentAgency) {
      console.log('🔄 AgencySettings - Mise à jour agence avec couleur:', color);

      // Préparer les données de mise à jour en incluant le nom pour garantir qu'il y a au moins un champ valide
      const updateData: any = {
        brand_color: color,
      };

      // Ajouter le nom actuel de l'agence pour s'assurer qu'il y a au moins un champ valide
      if (currentAgency.name) {
        updateData.name = currentAgency.name;
      }

      console.log('📦 Données de mise à jour envoyées:', updateData);

      updateAgency(currentAgency.id, updateData).then(success => {
        if (success) {
          console.log('✅ AgencySettings - Couleur sauvegardée avec succès');
          // Forcer la mise à jour du formData
          setFormData(prev => {
            console.log('🔄 AgencySettings - Mise à jour formData avec nouvelle couleur:', color);
            return { ...prev, brand_color: color };
          });
        } else {
          console.error('❌ AgencySettings - Erreur lors de la sauvegarde de la couleur');
          alert('Erreur lors de la sauvegarde de la couleur');
        }
      }).catch(error => {
        console.error('❌ AgencySettings - Erreur lors de la sauvegarde de la couleur:', error);
        alert('Erreur lors de la sauvegarde de la couleur');
      });
    }
  };

  if (!currentAgency) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">
          Chargement des informations de l'agence...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        {/* Informations de base */}
        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Nom de l'agence
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              placeholder="Nom de votre agence"
            />
          </div>

          <div>
            <label
              htmlFor="contact_phone"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Téléphone de contact
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              placeholder="+213 XX XX XX XX XX"
            />
          </div>

          <div>
            <label
              htmlFor="contact_email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Email de contact
            </label>
            <input
              type="email"
              id="contact_email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              placeholder="contact@votre-agence.com"
            />
          </div>
        </div>

        {/* Adresse et localisation */}
        <div>
          <label
            htmlFor="address"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Adresse
          </label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
            placeholder="Adresse de l'agence"
          />
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Ville
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              placeholder="Ville de l'agence"
            />
          </div>

          <div>
            <label
              htmlFor="wilaya"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Wilaya
            </label>
            <WilayaSelector
              name="wilaya"
              value={formData.wilaya}
              onChange={handleInputChange}
              placeholder="Sélectionner une wilaya"
            />
          </div>

          <div>
            <label
              htmlFor="website_url"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Site web
            </label>
            <input
              type="url"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              placeholder="https://www.votre-agence.com"
            />
          </div>
        </div>

        {/* Plan d'abonnement */}
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label
              htmlFor="subscription_plan"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Plan d'abonnement
            </label>
            <select
              id="subscription_plan"
              name="subscription_plan"
              value={formData.subscription_plan}
              onChange={handleSelectChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              disabled
            >
              <option value="basic">Basic</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Contactez l'administrateur pour changer de plan
            </p>
          </div>
        </div>

        {/* Code couleur de l'agence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Code couleur de l'agence
          </label>
          <div className="mt-2 flex items-center gap-4">
            <div
              className="h-12 w-12 rounded-lg border-2 border-gray-300 shadow-sm cursor-pointer hover:border-gray-400 transition-colors"
              style={{ backgroundColor: formData.brand_color }}
              onClick={() => setShowColorPicker(!showColorPicker)}
              title="Cliquer pour changer la couleur"
            />
            <div>
              <p className="font-medium text-gray-900 dark:text-white">
                {formData.brand_color.toUpperCase()}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Couleur utilisée dans l'interface et les documents
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="flex items-center gap-2"
            >
              <Palette className="h-4 w-4" />
              {showColorPicker ? 'Fermer' : 'Modifier'}
            </Button>
          </div>
          
          {showColorPicker && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Couleur actuelle : {formData.brand_color} | Agence : {currentAgency?.brand_color}
              </p>
              <ColorPicker
                key={`color-picker-${currentAgency?.brand_color || formData.brand_color}`}
                defaultColor={formData.brand_color}
                onChange={handleColorChange}
                onSave={handleColorSave}
              />
            </div>
          )}
        </div>

        {/* Logo de l'agence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Logo de l'agence
          </label>
          <div className="mt-2 flex items-center gap-4">
            <div className="h-24 w-24 overflow-hidden rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              {logo ? (
                <img
                  src={URL.createObjectURL(logo)}
                  alt="Logo preview"
                  className="h-full w-full object-cover"
                />
              ) : currentAgency?.logo_url ? (
                <img
                  src={`${(import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api').replace('/api', '')}${currentAgency.logo_url}`}
                  alt="Logo actuel"
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    console.error('Erreur chargement logo:', currentAgency.logo_url);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-gray-50 dark:bg-gray-700">
                  <Upload className="h-8 w-8 text-gray-400" />
                </div>
              )}
            </div>
            <div>
              <Button
                variant="secondary"
                onClick={() => document.getElementById('logo-upload')?.click()}
              >
                {currentAgency?.logo_url ? 'Changer le logo' : 'Ajouter un logo'}
              </Button>
              <input
                id="logo-upload"
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg"
                onChange={handleLogoChange}
              />
              <p className="mt-2 text-sm text-gray-500">
                PNG ou JPG. Max 2MB.
              </p>
              {logo && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                  ✓ Nouveau logo sélectionné (sera sauvegardé avec les autres modifications)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bouton de sauvegarde */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </div>
      </div>
    </div>
  );
};